/**
 * Design Sync Service
 * 
 * PSYCHOLOGY: Seamless transition from guest to user
 * UX PRINCIPLE: Auto-sync local designs after login
 * SOLID: Single responsibility - only handles design syncing
 * KISS: Simple sync logic with clear error handling
 * 
 * Flow:
 * 1. User logs in
 * 2. Check for local guest design
 * 3. Sync to backend
 * 4. Clear local storage
 * 5. Show success message
 */

import { toast } from 'react-hot-toast';
import { showConfetti } from '../utils/showConfetti';
import { prepareDesignForSave } from '../utils/designHelpers';
import { DesignSide } from '../types/design.types';
import { store } from '../store/store';
import { createDesign } from '../store/designSlice';

const LOCAL_DESIGN_KEY = 'qumashi_guest_design';

interface LocalDesign {
  canvasData: {
    frontCanvasJSON: any;
    backCanvasJSON: any;
  };
  timestamp: string;
  id: string;
}

class DesignSyncService {
  /**
   * Check if there's a local guest design to sync
   */
  hasLocalDesign(): boolean {
    const saved = localStorage.getItem(LOCAL_DESIGN_KEY);
    return !!saved;
  }

  /**
   * Get local guest design
   */
  getLocalDesign(): LocalDesign | null {
    try {
      const saved = localStorage.getItem(LOCAL_DESIGN_KEY);
      return saved ? JSON.parse(saved) : null;
    } catch (error) {
      console.error('Failed to get local design:', error);
      return null;
    }
  }

  /**
   * Sync local design to backend after login
   * Called automatically after successful authentication
   */
  async syncAfterLogin(): Promise<void> {
    const localDesign = this.getLocalDesign();
    
    if (!localDesign) {
      console.log('No local design to sync');
      return;
    }

    // Validate design has content
    const hasContent = 
      (localDesign.canvasData.frontCanvasJSON?.objects?.length > 0) || 
      (localDesign.canvasData.backCanvasJSON?.objects?.length > 0);

    if (!hasContent) {
      this.clearLocalDesign();
      return;
    }

    // Show loading toast
    const loadingToast = toast.loading('Syncing your design...');

    try {
      // Prepare design payload
      const designPayload = prepareDesignForSave(
        `My Design - ${new Date().toLocaleDateString()}`,
        localDesign.canvasData.frontCanvasJSON,
        localDesign.canvasData.backCanvasJSON,
        {
          productType: 'tshirt',
          color: '#ffffff',
          defaultSide: DesignSide.FRONT,
        }
      );

      // Save to backend
      const resultAction = await store.dispatch(createDesign(designPayload));

      if (createDesign.fulfilled.match(resultAction)) {
        // Success!
        toast.dismiss(loadingToast);
        showConfetti();
        
        toast.success(
          '✅ Design saved securely!',
          {
            description: 'Your creativity is now safe in your account.',
            duration: 5000
          }
        );

        // Clear local storage
        this.clearLocalDesign();

        // Optional: Send email notification (handled by backend)
        this.notifyDesignSaved(resultAction.payload);
      } else {
        // Failed
        toast.dismiss(loadingToast);
        toast.error(
          'Could not sync design automatically',
          {
            description: 'Don\'t worry! Your design is saved locally. You can save it manually.',
            duration: 7000
          }
        );
      }
    } catch (error) {
      toast.dismiss(loadingToast);
      console.error('Design sync error:', error);
      
      toast.error(
        'Sync failed',
        {
          description: 'Your design is safe locally. Please save it manually from the editor.',
          duration: 7000
        }
      );
    }
  }

  /**
   * Trigger email notification (backend handles actual sending)
   */
  private notifyDesignSaved(design: any): void {
    // Backend will send email automatically after design creation
    // This method can be used for additional client-side notifications
    console.log('Design saved notification triggered:', design.id);
  }

  /**
   * Clear local guest design
   */
  clearLocalDesign(): void {
    localStorage.removeItem(LOCAL_DESIGN_KEY);
    localStorage.removeItem('qumashi_guest_id');
  }

  /**
   * Restore design to canvas from local storage
   * Used when user wants to continue editing before syncing
   */
  restoreLocalDesign() {
    const localDesign = this.getLocalDesign();
    
    if (!localDesign) {
      return null;
    }

    return localDesign.canvasData;
  }
}

// Export singleton instance
export const designSyncService = new DesignSyncService();

